import React from 'react';

import { create } from 'zustand';

import { ENTITY_TYPES, NODE_TYPES } from '@/shared/api/openmetadata';
import type { OpenMetadataResponse, OpenMetadataSource } from '@/shared/api/openmetadata';
import type { AssetTreeNode } from '@/shared/lib/tree';
import { buildChildNodes, findNodeByKey, updateNodeChildren } from '@/shared/lib/tree';

type ExpandingNode = {
  key: string;
  entityType: string;
  index: string;
  useAggregation: boolean;
  entityTypes?: string[];
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
};

type TreeExpandStore = {
  treeData: AssetTreeNode[];
  expandedKeys: React.Key[];
  loadingKeys: React.Key[];
  expandingNode: ExpandingNode | null;
  loadedNodes: Set<string>;

  setTreeData: (treeData: AssetTreeNode[]) => void;
  setExpandedKeys: (keys: React.Key[]) => void;
  setLoadingKeys: (keys: React.Key[]) => void;
  setExpandingNode: (node: ExpandingNode | null) => void;
  addLoadingKey: (key: React.Key) => void;
  removeLoadingKey: (key: React.Key) => void;
  addExpandedKey: (key: React.Key) => void;
  removeExpandedKey: (key: React.Key) => void;
  addLoadedNode: (key: string) => void;
  hasLoadedNode: (key: string) => boolean;

  onExpand: (newExpandedKeys: React.Key[], info: { expanded: boolean; node: unknown }) => void;
  collapseAll: () => void;

  updateNodeChildren: (nodeChildren: OpenMetadataResponse, expandingNode: ExpandingNode) => void;
  handleExpandError: (expandingNode: ExpandingNode) => void;
  resetStore: () => void;
};

export const useTreeExpandStore = create<TreeExpandStore>((set, get) => ({
  treeData: [],
  expandedKeys: [],
  loadingKeys: [],
  expandingNode: null,
  loadedNodes: new Set(),

  setTreeData: (treeData) => {
    set({ treeData });
  },

  setExpandedKeys: (keys) => {
    set({ expandedKeys: keys });
  },

  setLoadingKeys: (keys) => {
    set({ loadingKeys: keys });
  },

  setExpandingNode: (node) => {
    set({ expandingNode: node });
  },

  addLoadingKey: (key) => {
    set((state) => ({
      loadingKeys: [...state.loadingKeys, key],
    }));
  },

  removeLoadingKey: (key) => {
    set((state) => ({
      loadingKeys: state.loadingKeys.filter((k) => k !== key),
    }));
  },

  addExpandedKey: (key) => {
    set((state) => {
      if (!state.expandedKeys.includes(key)) {
        return { expandedKeys: [...state.expandedKeys, key] };
      }
      return state;
    });
  },

  removeExpandedKey: (key) => {
    set((state) => ({
      expandedKeys: state.expandedKeys.filter((k) => k !== key),
    }));
  },

  addLoadedNode: (key) => {
    set((state) => ({
      loadedNodes: new Set([...state.loadedNodes, key]),
    }));
  },

  hasLoadedNode: (key) => {
    return get().loadedNodes.has(key);
  },

  onExpand: (newExpandedKeys, info) => {
    const { expanded, node } = info as { expanded: boolean; node: { key: string } };

    set({ expandedKeys: newExpandedKeys });

    if (expanded) {
      const { treeData, loadedNodes } = get();
      const targetNode = findNodeByKey(treeData, node.key);

      if (targetNode && !loadedNodes.has(targetNode.key) && !targetNode.isLeaf) {
        if (targetNode.children && targetNode.children.length > 0) {
          get().addLoadedNode(targetNode.key);
          return;
        }

        get().addLoadingKey(targetNode.key);

        if (targetNode.type === NODE_TYPES.ENTITY_TYPE) {
          const nodeData = targetNode.data as {
            entityType: string;
            index: string;
            useAggregation: boolean;
            entityTypes?: string[];
          };

          get().setExpandingNode({
            key: targetNode.key,
            entityType: nodeData.entityType,
            index: nodeData.index,
            useAggregation: nodeData.useAggregation,
            entityTypes: nodeData.entityTypes,
          });
        } else if (targetNode.type === NODE_TYPES.SERVICE_TYPE) {
          const nodeData = targetNode.data as {
            parentEntityType?: string;
            parentIndex?: string;
            serviceType: string;
            governanceType?: string;
          };

          if (nodeData.governanceType) {
            return;
          }

          get().setExpandingNode({
            key: targetNode.key,
            entityType: nodeData.parentEntityType || '',
            index: nodeData.parentIndex || 'dataAsset',
            useAggregation: true,
            serviceType: nodeData.serviceType,
          });
        } else if (targetNode.type === NODE_TYPES.SERVICE) {
          const nodeData = targetNode.data as {
            serviceName: string;
            serviceType: string;
            parentIndex: string;
            parentEntityType?: string;
          };

          if (
            nodeData.parentEntityType === ENTITY_TYPES.MLMODEL ||
            nodeData.parentEntityType === ENTITY_TYPES.CONTAINER
          ) {
            return;
          }

          get().setExpandingNode({
            key: targetNode.key,
            entityType: ENTITY_TYPES.DATABASE,
            index: nodeData.parentIndex,
            useAggregation: false,
            serviceType: nodeData.serviceType,
            serviceName: nodeData.serviceName,
          });
        } else if (targetNode.type === NODE_TYPES.DATABASE) {
          const nodeData = targetNode.data as OpenMetadataSource;

          get().setExpandingNode({
            key: targetNode.key,
            entityType: ENTITY_TYPES.SCHEMA,
            index: 'dataAsset',
            useAggregation: true,
            serviceType: nodeData.serviceType,
            serviceName: nodeData.service?.displayName,
            databaseName: nodeData.displayName || nodeData.name,
          });
        } else if (targetNode.type === NODE_TYPES.SCHEMA) {
          const nodeData = targetNode.data as {
            schemaName: string;
            serviceType: string;
            serviceName: string;
            databaseName: string;
          };

          get().setExpandingNode({
            key: targetNode.key,
            entityType: '',
            index: 'dataAsset',
            useAggregation: true,
            serviceType: nodeData.serviceType,
            serviceName: nodeData.serviceName,
            databaseName: nodeData.databaseName,
            schemaName: nodeData.schemaName,
          });
        }
      }
    }
  },

  collapseAll: () => {
    set({ expandedKeys: [] });
  },

  updateNodeChildren: (nodeChildren: OpenMetadataResponse, expandingNode: ExpandingNode) => {
    const { loadedNodes } = get();

    if (loadedNodes.has(expandingNode.key)) {
      get().setExpandingNode(null);
      return;
    }

    const childNodes = buildChildNodes(nodeChildren, expandingNode);

    set((state) => ({
      treeData: updateNodeChildren(state.treeData, expandingNode.key, childNodes),
      loadedNodes: new Set([...state.loadedNodes, expandingNode.key]),
    }));

    get().addExpandedKey(expandingNode.key);
    get().removeLoadingKey(expandingNode.key);
    get().setExpandingNode(null);
  },

  handleExpandError: (expandingNode) => {
    get().removeLoadingKey(expandingNode.key);
    get().removeExpandedKey(expandingNode.key);
    get().setExpandingNode(null);
  },

  resetStore: () => {
    set({
      treeData: [],
      expandedKeys: [],
      loadingKeys: [],
      expandingNode: null,
      loadedNodes: new Set(),
    });
  },
}));
